import React, { useRef, useEffect } from "react";
import NotificationItem from "../../notificationItem/NotificationItem";
import "../notificationModal/NotificationModal.css";

const BellIcon = () => (
  <svg
    width="48"
    height="48"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const Spinner = () => (
  <div className="nm-spinner" aria-label="Loading notifications">
    <div className="nm-spinner-ring" />
  </div>
);

const NotificationModal = ({
  notifications,
  displayed,
  unreadCount,
  newCount,
  activeTab,
  setActiveTab,
  loading,
  hasMore,
  loadMore,
  markAsRead,
  markAllAsRead,
  removeNotification,
  onClose,
}) => {
  const modalRef = useRef(null);

  // ── Outside click / tap — closes the modal ──────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };

    // Delay listener setup so the tap that OPENED the modal doesn't
    // immediately trigger this handler (covers both mouse and touch).
    // 300 ms > the 200 ms delay in MobileDrawer.handleNotificationsClick,
    // so the panel is fully settled before we start listening.
    const id = setTimeout(() => {
      document.addEventListener("mousedown", handler);
      document.addEventListener("touchend", handler); // ← mobile tap support
    }, 300);

    return () => {
      clearTimeout(id);
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchend", handler);
    };
  }, [onClose]);

  // ── Escape key ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  // "View all" switches to All Inbox tab — does NOT close the modal
  const handleViewAll = () => setActiveTab("all");

  return (
    <div
      className="nm-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Notifications"
    >
      <div className="nm-modal" ref={modalRef}>
        {/* Header */}
        <div className="nm-header">
          <h3 className="nm-title">Notifications</h3>
          <div className="nm-header-actions">
            {unreadCount > 0 && (
              <button className="nm-mark-all" onClick={markAllAsRead}>
                Mark all as read
              </button>
            )}
            <button
              className="nm-close-btn"
              onClick={onClose}
              aria-label="Close notifications"
            >
              ×
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="nm-tabs" role="tablist">
          <button
            className={`nm-tab${activeTab === "new" ? " nm-tab--active" : ""}`}
            role="tab"
            aria-selected={activeTab === "new"}
            onClick={() => setActiveTab("new")}
          >
            New
            {newCount > 0 && (
              <span className="nm-tab-badge">
                {newCount > 99 ? "99+" : newCount}
              </span>
            )}
          </button>
          <button
            className={`nm-tab${activeTab === "all" ? " nm-tab--active" : ""}`}
            role="tab"
            aria-selected={activeTab === "all"}
            onClick={() => setActiveTab("all")}
          >
            All Inbox
          </button>
        </div>

        {/* List */}
        <div className="nm-list" role="tabpanel">
          {loading && displayed.length === 0 ? (
            <Spinner />
          ) : displayed.length === 0 ? (
            <div className="nm-empty">
              <span className="nm-empty-icon">
                <BellIcon />
              </span>
              <p>
                {activeTab === "new"
                  ? "You're all caught up!"
                  : "No notifications yet."}
              </p>
              {activeTab === "new" && notifications.length > 0 && (
                <button className="nm-empty-view-all" onClick={handleViewAll}>
                  View all notifications
                </button>
              )}
            </div>
          ) : (
            <>
              {displayed.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onRead={markAsRead}
                  onRemove={removeNotification}
                />
              ))}
              {hasMore && (
                <div className="nm-load-more">
                  <button
                    className="nm-load-more-btn"
                    onClick={loadMore}
                    disabled={loading}
                  >
                    {loading ? "Loading…" : "Load more"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {activeTab === "new" && notifications.length > 0 && (
          <div className="nm-footer">
            <button className="nm-view-all" onClick={handleViewAll}>
              View all notifications
              <span className="nm-footer-count">({notifications.length})</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationModal;
